import { useState, useCallback, useRef, useEffect } from "react";

/**
 * Persistent column widths with drag-to-resize handles.
 * Widths are stored in localStorage keyed by `storageKey`.
 *
 * Usage:
 *   const { widths, getResizeHandle, reset, autoFitAll } = usePersistentColumnWidths(
 *     "estimate-editor-col-widths",
 *     { type: 52, product: 144, qty: 90, ... },
 *     { minWidth: 40, autoFit: { type: 52, product: 144, ... } }
 *   );
 *   <span style={{ width: widths.type }}>Type</span>
 *   {getResizeHandle("type")}   // single-click + drag = resize, double-click = auto-fit
 */
export function usePersistentColumnWidths(
  storageKey: string,
  defaults: Record<string, number>,
  opts?: { minWidth?: number; autoFit?: Record<string, number> }
) {
  const minWidth = opts?.minWidth ?? 40;
  const autoFitMap = opts?.autoFit ?? defaults;

  const [widths, setWidths] = useState<Record<string, number>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        return { ...defaults, ...parsed };
      }
    } catch {}
    return { ...defaults };
  });

  // Persist on change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(widths));
    } catch {}
  }, [storageKey, widths]);

  const dragRef = useRef<{ key: string; startX: number; startW: number; moved: boolean } | null>(null);

  const onMouseDown = useCallback(
    (key: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const startW = widths[key] ?? defaults[key] ?? 100;
      dragRef.current = { key, startX: e.clientX, startW, moved: false };

      const onMouseMove = (ev: MouseEvent) => {
        if (!dragRef.current) return;
        const delta = ev.clientX - dragRef.current.startX;
        if (Math.abs(delta) > 2) dragRef.current.moved = true;
        const newW = Math.max(minWidth, dragRef.current.startW + delta);
        setWidths((prev) => ({ ...prev, [dragRef.current!.key]: newW }));
      };
      const onMouseUp = () => {
        dragRef.current = null;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [widths, defaults, minWidth]
  );

  const autoFit = useCallback(
    (key: string, px?: number) => {
      const target = px ?? autoFitMap[key] ?? defaults[key] ?? 100;
      setWidths((prev) => ({ ...prev, [key]: Math.max(minWidth, target) }));
    },
    [autoFitMap, defaults, minWidth]
  );

  const autoFitAll = useCallback(
    (map?: Record<string, number>) => {
      const src = map ?? autoFitMap;
      setWidths((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(src)) {
          next[k] = Math.max(minWidth, src[k]);
        }
        return next;
      });
    },
    [autoFitMap, minWidth]
  );

  const getResizeHandle = useCallback(
    (key: string) => (
      <span
        onMouseDown={(e) => onMouseDown(key, e)}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          autoFit(key);
        }}
        className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/50 active:bg-primary"
        style={{ zIndex: 30 }}
        aria-hidden
        title="Drag to resize · Double-click to auto-fit"
      />
    ),
    [onMouseDown, autoFit]
  );

  const reset = useCallback(() => {
    setWidths({ ...defaults });
  }, [defaults]);

  return { widths, getResizeHandle, reset, autoFit, autoFitAll };
}
